/*
 * @providesModule WithdrawalController
 *
 */

import CurrentUserStore from 'CurrentUserStore';
import NetClient from 'NetClient';
import SKZConstants from 'SKZConstants';

import {NativeModules} from 'react-native';
const crypto = require('crypto');
const OnError = NativeModules.SKZAPIManager.OnError;

type WithdrawRequestBody = {
  type: string;
  amount: number;
  bank_account_id?: string;
  paypal_email?: string;
};

class WithdrawalController {
  withdraw(method: string, amount: number, payPalEmail?: string, bankAccountId?: string): Promise {
    console.debug('WithdrawalApiController: withdraw');
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        const path = `users/${userId}/transactions`;

        const body: WithdrawRequestBody = {
          type: method == SKZConstants.PAYPAL ? 'PAYPAL_WITHDRAWAL' : 'WITHDRAWAL',
          amount,
        };

        if (method == SKZConstants.PAYPAL && payPalEmail) {
          body.paypal_email = payPalEmail;
        }

        if (method === SKZConstants.AEROPAY_WITHDRAWAL && bankAccountId) {
          body.type = SKZConstants.AEROPAY_WITHDRAWAL;
          body.bank_account_id = bankAccountId;
        }

        if (method === SKZConstants.AEROPAY_RTP_WITHDRAWAL && bankAccountId) {
          body.type = SKZConstants.AEROPAY_RTP_WITHDRAWAL;
          body.bank_account_id = bankAccountId;
        }

        if (userId) {
          NetClient.fetchTransactionURL(
            'POST',
            path,
            body,
            true,
            OnError.failSilently,
            (error: Object, payload: Object) => {
              if (payload && !error) {
                resolve(payload);
              } else {
                reject(error);
              }
            }
          );
        }
      });
    });
  }

  nonUSDWithdraw(
    cashBalance: number,
    bonusBalance: number,
    amount: number,
    email: string
  ): Promise {
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        const path = '/payment/v2/withdrawal-requests';
        const body = {
          type: 'INTL_MANUAL',
          amount: amount,
          email: email,
        };
        const currentDate = new Date();
        const uuid =
          userId +
          cashBalance +
          bonusBalance +
          amount +
          currentDate.getFullYear() +
          (currentDate.getMonth() + 1) +
          currentDate.getDate() +
          currentDate.getHours() +
          currentDate.getMinutes();
        const transactionId = this.uuidHash(uuid);
        const headers = {'x-skillz-transaction-id': transactionId};

        NetClient.postURLWithHeaders(
          path,
          body,
          headers,
          OnError.failSilently,
          (error: Object, payload: Object) => {
            if (!error) {
              resolve();
            } else {
              reject(error);
            }
          }
        );
      });
    });
  }

  fetchPendingPayPalWithdrawals(): Promise {
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        const path = `users/${userId}/paypal_withdrawal_requests`;

        NetClient.getURL(path, null, OnError.failSilently)
          .then((payload: Object) => {
            if (payload && payload.open_requests) {
              resolve(payload);
            } else {
              const temp = {
                ...payload,
                open_requests: [],
              };

              resolve(temp);
            }
          })
          .catch((error: unknown) => {
            console.debug('Error in fetchPendingPayPalWithdrawals - WithdrawalController', error);
            reject(error);
          });
      });
    });
  }

  deletePendingPayPalWithdrawal(id: string): Promise {
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        const path = `users/${userId}/paypal_withdrawal_requests/${id}`;

        NetClient.deleteURL(path, null, OnError.failSilently, (error: unknown, payload: Object) => {
          if (payload && !error) {
            console.debug('deletePendingPayPalWithdrawal - WithdrawalController', payload);
            resolve(payload);
          } else {
            console.debug('Error in deletePendingPayPalWithdrawal - WithdrawalController', error);
            reject(error);
          }
        });
      });
    });
  }

  deleteAllPendingPayPalWithdrawals(): Promise {
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        this.fetchPendingPayPalWithdrawals()
          .then((pendingWithdrawals: Object) => {
            const requests = [];

            pendingWithdrawals.open_requests.forEach((request: Object) => {
              requests.push(this.deletePendingPayPalWithdrawal(request.id));
            });

            Promise.all(requests)
              .then((status: Array<Object>) => {
                console.debug(
                  'Delete all pending paypal withdrawal requests - WithdrawalController',
                  status
                );
                resolve(status);
              })
              .catch((error: unknown) => {
                console.debug(
                  'Error trying to delete all pending paypal withdrawal requests - WithdrawalController',
                  error
                );
                reject(error);
              });
          })
          .catch((error: unknown) => {
            reject(error);
            console.debug(
              'Error fetching all pending paypal withdrawal requests from deleteAllPendingPayPalWithdrawals method - WithdrawalController',
              error
            );
          });
      });
    });
  }

  uuidHash(input: string): string {
    const md5Bytes = crypto.createHash('md5').update(input).digest();
    md5Bytes[6] &= 0x0f; // clear version
    md5Bytes[6] |= 0x30; // set to version 3
    md5Bytes[8] &= 0x3f; // clear variant
    md5Bytes[8] |= 0x80; // set to IETF variant
    const hex = md5Bytes.toString('hex');
    const uuid = hex.replace(/(\w{8})(\w{4})(\w{4})(\w{4})(\w{12})/, '$1-$2-$3-$4-$5');
    return uuid;
  }
}

export default new WithdrawalController();
